import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, CanActivateChild } from '@angular/router';
import { Observable, filter, from, of, switchMap } from 'rxjs';
import { DataService } from '../service/data.service';
import { CompanyService } from '../service/company.service';
import { UserCompaniesService } from '../service/user-companies.service';
import { MkgUser } from '../interface/user';

@Injectable({
  providedIn: 'root'
})
export class RoleGuard implements CanActivate, CanActivateChild {

  /** companies the server already confirmed as owned by the user, so the check runs once per session */
  private _confirmedOwned = new Set<string>();

  /** company whose access was already registered while the user stays in it */
  private _lastRegisteredCompany: string | null = null;

  constructor(
    private _dataService: DataService,
    private _companyService: CompanyService,
    private _userCompanies: UserCompaniesService,
  ) { }

  canActivate(route: ActivatedRouteSnapshot): Observable<boolean> {
    return this._dataService.user$.pipe(
      filter(user => !!user),
      switchMap(user => {
        const companyId = route.paramMap.get('company');
        if (!companyId) {
          return of(true);
        }
        return from(this._hasAccess(user, companyId));
      })
    );
  }

  /**
   * Runs for every child of the `company` route, whose own params carry the company id (the parent route has none).
   * Registers the access once per stay in a company; the server only records companies the user is linked to.
   * Never blocks the navigation.
   */
  canActivateChild(childRoute: ActivatedRouteSnapshot): boolean {
    const companyId = childRoute.paramMap.get('company');
    if (!companyId) {
      // company list or another screen without a company: the next company entered counts again
      this._lastRegisteredCompany = null;
    } else if (companyId !== this._lastRegisteredCompany) {
      this._lastRegisteredCompany = companyId;
      this._registerAccess(companyId);
    }
    return true;
  }

  /**
   * Only the owner or a user with a role in the company enters. The shared lists decide when they can;
   * when the API cut them (50 companies) the owner is confirmed on the server.
   */
  private async _hasAccess(user: MkgUser, companyId: string): Promise<boolean> {
    if (user.roles.some(r => r._id === companyId) || user.ownCompanies$.value.some(r => r.id === companyId)) {
      return true;
    }
    if (!this._userCompanies.truncated()) {
      return false;
    }
    if (this._confirmedOwned.has(companyId)) {
      return true;
    }
    try {
      const { companies } = await this._companyService.list({ company: companyId, scope: this._userCompanies.scope });
      const isOwner = companies.some(c => c.id === companyId && user.isOwnerOf(c));
      if (isOwner) {
        this._confirmedOwned.add(companyId);
      }
      return isOwner;
    } catch (error) {
      return false;
    }
  }

  /** Fire and forget: the access list never delays or blocks the navigation */
  private _registerAccess(companyId: string): void {
    try {
      Promise.resolve(this._companyService.registerAccess(companyId)).catch(() => { });
    } catch (error) {
      // ignored on purpose
    }
  }
}
